/**
 * Content slideshow (slider_slideshow): GSAP fade/swipe, autoplay,
 * visible counter, pointer swipe, keyboard, reduced motion, slide videos.
 */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const SWIPE_THRESHOLD = 48;
const SWIPE_SLIDE_RATIO = 0.9;
const SWIPE_INACTIVE_ALPHA = 0.35;
const CONTENT_OUT_SEC = 0.38;
const CONTENT_IN_SEC = 0.52;
const CONTENT_STAGGER = 0.07;
const CONTENT_SHIFT_Y = 22;
const CONTENT_SHIFT_X = 28;
const CONTENT_ITEM_SELECTOR = '.slider-slideshow__content > *';

/**
 * Natural scroll-in reveal shared by both styles: the block gently rises and
 * fades in once as it enters the viewport. Targets a wrapper so the internal
 * per-slide opacity management is untouched.
 *
 * @param {HTMLElement} root
 */
function initScrollReveal(root) {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) {
    return;
  }

  // Style 2 lives inside a .container wrap; reveal the whole framed block.
  // Style 1 is full-bleed, so the root itself is the reveal target.
  const target =
    root.dataset.style === '2' ? root.closest('.slider-slideshow__wrap') || root : root;

  gsap.fromTo(
    target,
    { autoAlpha: 0, y: 56 },
    {
      autoAlpha: 1,
      y: 0,
      duration: 1.0,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: root,
        start: 'top 82%',
        once: true,
      },
      onComplete: () => gsap.set(target, { clearProps: 'transform,opacity,visibility' }),
    }
  );
}

/**
 * @param {HTMLElement} slideEl
 * @param {boolean} active
 */
function syncSlideVideo(slideEl, active) {
  const video = slideEl.querySelector('[data-slide-video]');
  if (!(video instanceof HTMLVideoElement)) {
    return;
  }
  if (active) {
    video.play().catch(() => {});
  } else {
    video.pause();
  }
}

/**
 * @param {number} index
 * @param {number} total
 */
function formatCounter(index, total) {
  const pad = Math.max(2, String(total).length);
  const current = String(index + 1).padStart(pad, '0');
  const max = String(total).padStart(pad, '0');
  return `${current}/${max}`;
}

/**
 * @param {HTMLElement} slideEl
 * @returns {HTMLElement[]}
 */
function getContentItems(slideEl) {
  const content = slideEl.querySelector('.slider-slideshow__content');
  return content ? [...content.querySelectorAll(CONTENT_ITEM_SELECTOR)] : [];
}

/**
 * @param {HTMLElement} slideEl
 * @returns {HTMLElement|null}
 */
function getSlideAsset(slideEl) {
  return slideEl.querySelector('.slider-slideshow__slide-asset');
}

/**
 * @param {HTMLElement} slideEl
 * @returns {HTMLElement|null}
 */
function getContentEl(slideEl) {
  return slideEl.querySelector('.slider-slideshow__content');
}

/**
 * @param {HTMLElement[]} items
 * @param {boolean} visible
 */
function setContentItemsState(items, visible) {
  if (!items.length) {
    return;
  }
  gsap.killTweensOf(items);
  if (visible) {
    gsap.set(items, { autoAlpha: 1, x: 0, y: 0, clearProps: 'transform' });
  } else {
    gsap.set(items, { autoAlpha: 0, x: 0, y: CONTENT_SHIFT_Y * 0.5 });
  }
}

/**
 * @param {HTMLElement} slideEl
 * @param {boolean} visible
 * @param {boolean} immediate
 */
function setSlideContentVisible(slideEl, visible) {
  const content = getContentEl(slideEl);
  const items = getContentItems(slideEl);
  if (content) {
    gsap.killTweensOf(content);
    gsap.set(content, {
      autoAlpha: visible ? 1 : 0,
      visibility: visible ? 'visible' : 'hidden',
    });
  }
  setContentItemsState(items, visible);
}

/**
 * @param {HTMLElement[]} slides
 * @param {number} activeIdx
 */
function syncAllSlideContent(slides, activeIdx) {
  slides.forEach((el, i) => {
    setSlideContentVisible(el, i === activeIdx);
  });
}

/**
 * @param {HTMLElement[]} slides
 * @param {number} activeIdx
 * @param {'fade' | 'swipe'} mode
 */
function snapSlidesToIndex(slides, activeIdx, mode = 'fade') {
  slides.forEach((el, i) => {
    const on = i === activeIdx;
    const asset = getSlideAsset(el);
    const content = getContentEl(el);
    const items = getContentItems(el);

    gsap.killTweensOf(el);
    if (asset) {
      gsap.killTweensOf(asset);
    }
    gsap.killTweensOf(items);
    if (content) {
      gsap.killTweensOf(content);
    }

    gsap.set(el, { autoAlpha: 1, x: 0, clearProps: 'transform' });
    if (asset) {
      const assetAlpha = on ? 1 : mode === 'swipe' ? SWIPE_INACTIVE_ALPHA : 0;
      gsap.set(asset, { autoAlpha: assetAlpha, x: 0, clearProps: 'transform' });
    }
    setSlideContentVisible(el, on);

    el.classList.toggle('is-active', on);
    el.setAttribute('aria-hidden', on ? 'false' : 'true');
    if ('inert' in el) {
      el.inert = !on;
    }
    syncSlideVideo(el, on);
  });
}

/**
 * @param {HTMLElement} prevEl
 * @param {HTMLElement} nextEl
 * @param {HTMLElement[]} slides
 * @param {{ immediate?: boolean, direction?: number, animation?: string, prefersReduced?: boolean, fadeSec?: number, transitionToken?: number, getTransitionToken?: () => number }} opts
 */
function transitionSlideContent(prevEl, nextEl, slides, opts = {}) {
  const {
    immediate = false,
    direction = 0,
    animation: animMode = 'fade',
    prefersReduced = false,
    fadeSec = 0.95,
    transitionToken = 0,
    getTransitionToken = () => transitionToken,
  } = opts;

  const prevItems = getContentItems(prevEl);
  const nextItems = getContentItems(nextEl);
  const prevContent = getContentEl(prevEl);
  const nextContent = getContentEl(nextEl);

  slides.forEach((slide) => {
    if (slide !== prevEl && slide !== nextEl) {
      setContentItemsState(getContentItems(slide), false);
    }
  });

  gsap.killTweensOf([...prevItems, ...nextItems]);

  if (immediate || prefersReduced) {
    if (prevContent) {
      gsap.set(prevContent, { autoAlpha: 0, visibility: 'hidden' });
    }
    if (nextContent) {
      gsap.set(nextContent, { autoAlpha: 1, visibility: 'visible' });
    }
    setContentItemsState(prevItems, false);
    setContentItemsState(nextItems, true);
    return;
  }

  const isSwipe = animMode === 'swipe';

  if (!isSwipe) {
    // Fade: stacked slides share one content area — hide all, then animate in next only.
    slides.forEach((slide) => {
      const content = getContentEl(slide);
      const items = getContentItems(slide);
      gsap.killTweensOf(items);
      if (content) {
        gsap.killTweensOf(content);
        gsap.set(content, { autoAlpha: 0, visibility: 'hidden' });
      }
      setContentItemsState(items, false);
    });

    if (nextContent) {
      gsap.set(nextContent, { autoAlpha: 1, visibility: 'visible' });
    }

    if (nextItems.length) {
      gsap.set(nextItems, {
        autoAlpha: 0,
        x: 0,
        y: CONTENT_SHIFT_Y,
        force3D: true,
        willChange: 'transform, opacity',
      });
      gsap.to(nextItems, {
        autoAlpha: 1,
        x: 0,
        y: 0,
        duration: CONTENT_IN_SEC,
        stagger: { each: CONTENT_STAGGER, from: 'start' },
        delay: fadeSec * 0.12,
        ease: 'power3.out',
        overwrite: 'auto',
        onComplete: () => {
          if (getTransitionToken() !== transitionToken) {
            return;
          }
          gsap.set(nextItems, { clearProps: 'transform,willChange' });
        },
      });
    }
    return;
  }

  if (nextContent) {
    gsap.set(nextContent, { autoAlpha: 1, visibility: 'visible' });
  }

  const enterAxis = direction < 0 ? -1 : 1;
  const exitX = isSwipe ? -enterAxis * CONTENT_SHIFT_X : 0;
  const enterX = isSwipe ? enterAxis * CONTENT_SHIFT_X : 0;
  const exitY = isSwipe ? 0 : -CONTENT_SHIFT_Y;
  const enterY = isSwipe ? 0 : CONTENT_SHIFT_Y;
  const enterDelay = fadeSec * (isSwipe ? 0.22 : 0.28);

  if (prevItems.length) {
    gsap.set(prevItems, { willChange: 'transform, opacity' });
    gsap.to(prevItems, {
      autoAlpha: 0,
      x: exitX,
      y: exitY,
      duration: CONTENT_OUT_SEC,
      stagger: { each: CONTENT_STAGGER * 0.4, from: 'end' },
      ease: 'power2.in',
      overwrite: 'auto',
      onComplete: () => {
        if (getTransitionToken() !== transitionToken) {
          return;
        }
        gsap.set(prevItems, { x: 0, y: CONTENT_SHIFT_Y * 0.5, clearProps: 'transform,willChange' });
      },
    });
  }

  if (nextItems.length) {
    gsap.set(nextItems, {
      autoAlpha: 0,
      x: enterX,
      y: enterY,
      force3D: true,
      willChange: 'transform, opacity',
    });
    gsap.to(nextItems, {
      autoAlpha: 1,
      x: 0,
      y: 0,
      duration: CONTENT_IN_SEC,
      stagger: { each: CONTENT_STAGGER, from: 'start' },
      delay: enterDelay,
      ease: 'power3.out',
      overwrite: 'auto',
      onComplete: () => {
        if (getTransitionToken() !== transitionToken) {
          return;
        }
        gsap.set(nextItems, { clearProps: 'transform,willChange' });
      },
    });
  }
}

/**
 * @param {HTMLElement} root
 */
function initOne(root) {
  if (root.dataset.r24ContentSlideshowMounted) {
    return;
  }
  root.dataset.r24ContentSlideshowMounted = '1';

  const slides = [...root.querySelectorAll('[data-r24-slide]')];
  const toolbar = root.querySelector('.r24-asset-slideshow__controls');
  const btn = root.querySelector('.r24-asset-slideshow__toggle');
  const btnPrev = root.querySelector('.r24-asset-slideshow__prev');
  const btnNext = root.querySelector('.r24-asset-slideshow__next');
  const statusEl = root.querySelector('.slider-slideshow__sr-only');
  const counterEl = root.querySelector('[data-r24-slide-counter]');

  if (slides.length === 0) {
    return;
  }

  const intervalMs = parseInt(String(root.dataset.interval || '8000'), 10) || 8000;
  const fadeMs = parseInt(String(root.dataset.fadeMs || '950'), 10) || 950;
  const fadeSec = fadeMs / 1000;
  const animation = root.dataset.animation === 'swipe' ? 'swipe' : 'fade';
  const track = root.querySelector('.slider-slideshow__track');
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Style 2 (framed): each slide fills the frame edge-to-edge (ratio 1), so
  // neighbors stay fully outside the frame (no peek). Style 1 (full-bleed) keeps
  // the peek look with partial-width slides.
  const isStyle2 = root.dataset.style === '2';
  const swipeRatio = isStyle2 ? 1 : SWIPE_SLIDE_RATIO;
  // Gap is owned by CSS (--slider-slideshow-slide-gap, forced to 0 for style 2
  // swipe). Read the resolved value once so the translate math always matches the
  // rendered flex gap instead of duplicating the constant here.
  const swipeGap = track ? parseFloat(getComputedStyle(track).columnGap) || 0 : 0;

  if (prefersReduced) {
    root.dataset.reducedMotion = '1';
  }

  let idx = slides.findIndex((el) => el.classList.contains('is-active'));
  if (idx < 0) {
    idx = 0;
  }

  let playing = slides.length > 1 && !prefersReduced;
  /** @type {ReturnType<typeof setInterval> | null} */
  let tick = null;
  let transitionToken = 0;

  function clearTick() {
    if (tick !== null) {
      clearInterval(tick);
      tick = null;
    }
  }

  function startTick() {
    clearTick();
    if (!playing || prefersReduced || slides.length < 2) {
      return;
    }
    tick = setInterval(() => {
      goTo(idx + 1, { userGesture: false });
    }, intervalMs);
  }

  function updateCounter(i) {
    if (!counterEl) {
      return;
    }
    counterEl.textContent = formatCounter(i, slides.length);
  }

  function syncVisibility() {
    slides.forEach((el, i) => {
      const on = i === idx;
      el.classList.toggle('is-active', on);
      el.setAttribute('aria-hidden', on ? 'false' : 'true');
      if ('inert' in el) {
        el.inert = !on;
      }
      syncSlideVideo(el, on);
    });
    updateCounter(idx);
  }

  function finishTransition(token) {
    if (token !== transitionToken) {
      return;
    }
    snapSlidesToIndex(slides, idx, animation);
    syncVisibility();
  }

  function announce(i) {
    if (!statusEl) {
      return;
    }
    statusEl.textContent = formatCounter(i, slides.length);
  }

  function getViewportWidth() {
    const viewport = root.querySelector('.slider-slideshow__viewport');
    return viewport?.clientWidth || root.clientWidth;
  }

  /**
   * @param {number} [slideIndex]
   */
  function getSwipeMetrics(slideIndex = idx) {
    const viewportW = getViewportWidth();
    const slideW = viewportW * swipeRatio;
    const gap = swipeGap;
    const stride = slideW + gap;
    const offsetX = (viewportW - slideW) / 2 - slideIndex * stride;
    const trackW = slides.length * slideW + Math.max(0, slides.length - 1) * gap;
    return { viewportW, slideW, gap, stride, offsetX, trackW };
  }

  /** @type {ResizeObserver | null} */
  let swipeResizeObserver = null;

  function layoutSwipeTrack() {
    if (!track || animation !== 'swipe') {
      return;
    }
    const { slideW, gap, offsetX, trackW } = getSwipeMetrics(idx);
    track.style.gap = `${gap}px`;
    track.style.width = `${trackW}px`;
    slides.forEach((slide) => {
      slide.style.width = `${slideW}px`;
      slide.style.flexBasis = `${slideW}px`;
    });
    gsap.set(track, { x: offsetX });
    slides.forEach((el, i) => {
      const asset = getSlideAsset(el);
      if (!asset) {
        return;
      }
      gsap.set(asset, { autoAlpha: i === idx ? 1 : SWIPE_INACTIVE_ALPHA });
    });
  }

  function setSwipeIndex(nextIdx, opts = {}) {
    const { immediate = false, userGesture = false, direction = 0 } = opts;
    if (!track) {
      return;
    }

    nextIdx = ((nextIdx % slides.length) + slides.length) % slides.length;
    const fromIdx = idx;
    if (nextIdx === fromIdx) {
      return;
    }

    const token = ++transitionToken;
    snapSlidesToIndex(slides, fromIdx, 'swipe');

    const prevEl = slides[fromIdx];
    const nextEl = slides[nextIdx];
    const { offsetX: targetX } = getSwipeMetrics(nextIdx);

    gsap.killTweensOf(track);

    transitionSlideContent(prevEl, nextEl, slides, {
      immediate,
      direction,
      animation,
      prefersReduced,
      fadeSec,
      transitionToken: token,
      getTransitionToken: () => transitionToken,
    });

    idx = nextIdx;
    syncVisibility();

    if (immediate || prefersReduced) {
      gsap.set(track, { x: targetX });
      finishTransition(token);
    } else {
      gsap.to(track, {
        x: targetX,
        duration: fadeSec,
        ease: 'power3.inOut',
        overwrite: 'auto',
        onComplete: () => finishTransition(token),
      });
    }

    slides.forEach((el, i) => {
      const asset = getSlideAsset(el);
      if (!asset) {
        return;
      }
      if (immediate || prefersReduced) {
        gsap.set(asset, { autoAlpha: i === idx ? 1 : SWIPE_INACTIVE_ALPHA });
        return;
      }
      gsap.to(asset, {
        autoAlpha: i === idx ? 1 : SWIPE_INACTIVE_ALPHA,
        duration: fadeSec * 0.65,
        ease: 'power2.inOut',
        overwrite: 'auto',
      });
    });

    if (userGesture) {
      announce(idx);
    }

    if (playing && !prefersReduced) {
      clearTick();
      startTick();
    }
  }

  function goToFade(nextIdx, opts = {}) {
    const { userGesture = false, direction = 0 } = opts;

    nextIdx = ((nextIdx % slides.length) + slides.length) % slides.length;
    const fromIdx = idx;
    if (nextIdx === fromIdx) {
      return;
    }

    const token = ++transitionToken;
    snapSlidesToIndex(slides, fromIdx, 'fade');

    const prevEl = slides[fromIdx];
    const nextEl = slides[nextIdx];
    const prevAsset = getSlideAsset(prevEl);
    const nextAsset = getSlideAsset(nextEl);

    idx = nextIdx;
    syncVisibility();

    transitionSlideContent(prevEl, nextEl, slides, {
      direction,
      animation,
      prefersReduced,
      fadeSec,
      transitionToken: token,
      getTransitionToken: () => transitionToken,
    });

    if (prefersReduced) {
      finishTransition(token);
      if (userGesture) {
        announce(idx);
      }
      return;
    }

    if (prevAsset) {
      gsap.set(prevAsset, { autoAlpha: 1, x: 0 });
    }
    if (nextAsset) {
      gsap.set(nextAsset, { autoAlpha: 0, x: 0 });
    }

    if (prevAsset && nextAsset) {
      gsap
        .timeline({
          overwrite: 'auto',
          onComplete: () => finishTransition(token),
        })
        .to(prevAsset, { autoAlpha: 0, duration: fadeSec, ease: 'power2.inOut' }, 0)
        .to(nextAsset, { autoAlpha: 1, duration: fadeSec, ease: 'power2.inOut' }, 0);
    } else if (nextAsset) {
      gsap.to(nextAsset, {
        autoAlpha: 1,
        duration: fadeSec,
        ease: 'power2.inOut',
        overwrite: 'auto',
        onComplete: () => finishTransition(token),
      });
    } else {
      finishTransition(token);
    }

    if (userGesture) {
      announce(idx);
    }

    if (playing && !prefersReduced) {
      clearTick();
      startTick();
    }
  }

  /**
   * @param {number} nextIdx
   * @param {{ userGesture?: boolean, direction?: number }} opts
   */
  function goTo(nextIdx, opts = {}) {
    const { userGesture = false, direction = 0 } = opts;
    if (slides.length < 2) {
      return;
    }

    const prev = idx;
    nextIdx = ((nextIdx % slides.length) + slides.length) % slides.length;
    if (nextIdx === prev) {
      return;
    }

    if (animation === 'swipe') {
      const isWrapForward = direction === 1 && nextIdx < prev;
      const isWrapBackward = direction === -1 && nextIdx > prev;
      if (isWrapForward || isWrapBackward) {
        setSwipeIndex(nextIdx, { immediate: true, userGesture, direction });
        return;
      }
      setSwipeIndex(nextIdx, { userGesture, direction });
      return;
    }

    goToFade(nextIdx, { userGesture, direction });
  }

  const labelPause = btn?.dataset.labelPause || 'Pause';
  const labelPlay = btn?.dataset.labelPlay || 'Play';

  function updateBtn() {
    if (!btn) {
      return;
    }
    const on = playing;
    btn.dataset.playing = on ? 'true' : 'false';
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute('aria-label', on ? labelPause : labelPlay);
  }

  if (animation === 'swipe') {
    root.classList.add('slider-slideshow--swipe');
    slides.forEach((el) => {
      gsap.set(el, { autoAlpha: 1, x: 0, clearProps: 'transform' });
    });
    layoutSwipeTrack();
    const resizeTarget = root.querySelector('.slider-slideshow__viewport') || root;
    swipeResizeObserver = new ResizeObserver(() => {
      layoutSwipeTrack();
      syncAllSlideContent(slides, idx);
    });
    swipeResizeObserver.observe(resizeTarget);
  } else {
    slides.forEach((el, i) => {
      gsap.set(el, { autoAlpha: 1, x: 0 });
      const asset = getSlideAsset(el);
      if (asset) {
        gsap.set(asset, { autoAlpha: i === idx ? 1 : 0, x: 0 });
      }
    });
  }

  syncAllSlideContent(slides, idx);

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (animation === 'swipe') {
        layoutSwipeTrack();
      }
      syncAllSlideContent(slides, idx);
      ScrollTrigger.refresh();
    });
  });
  syncVisibility();

  if (btn) {
    btn.addEventListener('click', () => {
      playing = !playing;
      updateBtn();
      if (playing && !prefersReduced) {
        startTick();
      } else {
        clearTick();
      }
    });
  }

  btnPrev?.addEventListener('click', () => {
    goTo(idx - 1, { userGesture: true, direction: -1 });
  });
  btnNext?.addEventListener('click', () => {
    goTo(idx + 1, { userGesture: true, direction: 1 });
  });

  let downX = null;
  root.addEventListener(
    'pointerdown',
    (e) => {
      if (toolbar && e.target instanceof Node && toolbar.contains(e.target)) {
        return;
      }
      // Don't capture pointer on links/buttons — setPointerCapture retargets
      // pointerup to the root and swallows the click (no navigation).
      if (
        e.target instanceof Element &&
        e.target.closest('a, button, input, textarea, select, label, [role="button"]')
      ) {
        return;
      }
      if (e.pointerType === 'mouse' && e.button !== 0) {
        return;
      }
      downX = e.clientX;
      try {
        root.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    { passive: true }
  );

  root.addEventListener(
    'pointerup',
    (e) => {
      if (downX === null) {
        return;
      }
      const dx = e.clientX - downX;
      downX = null;
      try {
        root.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (Math.abs(dx) < SWIPE_THRESHOLD) {
        return;
      }
      if (dx < 0) {
        goTo(idx + 1, { userGesture: true, direction: 1 });
      } else {
        goTo(idx - 1, { userGesture: true, direction: -1 });
      }
    },
    { passive: true }
  );

  root.addEventListener('pointercancel', () => {
    downX = null;
  });

  root.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(idx + 1, { userGesture: true, direction: 1 });
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(idx - 1, { userGesture: true, direction: -1 });
    }
  });

  if (slides.length > 1) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (playing && !prefersReduced) {
              startTick();
            }
          } else {
            clearTick();
          }
        });
      },
      { root: null, threshold: 0.08 }
    );
    io.observe(root);
  }

  initScrollReveal(root);

  updateBtn();
  if (playing && !prefersReduced) {
    startTick();
  }
}

export function initContentSlideshows() {
  document.querySelectorAll('[data-r24-content-slideshow]').forEach(initOne);
}
